import * as Sentry from '@sentry/nextjs'

import { store } from '@/redux/store'
import {
  clearAuthTokens,
  selectTokens,
  setAuthTokens,
} from '@/redux/token/tokenSlice'

import { tokenManager } from './tokenManager'
import { AuthClientTokens } from './types'

export type TokenStorageMode = 'legacy' | 'memory-only'

let storageMode: TokenStorageMode = 'legacy'

const persistKey = 'persist:alpha-v1'

const removeTokenFromPersistedStorage = () => {
  try {
    const raw = window.localStorage.getItem(persistKey)
    if (!raw) return

    const persisted = JSON.parse(raw)
    delete persisted.token
    window.localStorage.setItem(persistKey, JSON.stringify(persisted))
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'tokenStorageBridge',
      },
    })
  }
}

export const setTokenStorageMode = (nextMode: TokenStorageMode) => {
  if (nextMode === storageMode) return

  // FF ON path: move to memory-only and wipe persisted Redux tokens.
  if (nextMode === 'memory-only') {
    const reduxTokens = selectTokens(store.getState())
    tokenManager.setTokens(reduxTokens)
    store.dispatch(clearAuthTokens())
    removeTokenFromPersistedStorage()
  }

  // FF OFF path: restore legacy Redux persistence from in-memory tokens.
  if (nextMode === 'legacy') {
    store.dispatch(setAuthTokens(tokenManager.getTokens()))
  }

  storageMode = nextMode
}

export const getTokenStorageMode = (): TokenStorageMode => storageMode

export const writeTokensForCurrentMode = (tokens: AuthClientTokens): void => {
  tokenManager.setTokens(tokens)

  if (storageMode === 'legacy') {
    store.dispatch(setAuthTokens(tokens))
    return
  }

  // FF ON path: ensure persisted Redux tokens stay cleared.
  store.dispatch(clearAuthTokens())
  removeTokenFromPersistedStorage()
}

export const clearTokensForCurrentMode = (): void => {
  tokenManager.clearTokens()
  store.dispatch(clearAuthTokens())
  removeTokenFromPersistedStorage()
}

export const getAccessTokenForCurrentMode = (): string | null => {
  if (storageMode === 'legacy') {
    return selectTokens(store.getState())?.token ?? null
  }

  return tokenManager.getAccessToken()
}

export const getIdTokenForCurrentMode = (): string | null => {
  if (storageMode === 'legacy') {
    return selectTokens(store.getState())?.idToken ?? null
  }

  return tokenManager.getIdToken()
}
